"use client";

import React from "react";
import { CircleCheck as CheckCircle2, Phone, Shield, Ruler } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function QuoteForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4">
        <div className="size-16 rounded-lg bg-secondary/20 flex items-center justify-center mb-4">
          <CheckCircle2 className="size-8 text-secondary" />
        </div>
        <h3 className="text-2xl font-default font-bold text-foreground">Request Received</h3>
        <p className="text-muted-foreground">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <FormField control={control} name="name" rules={{
      required: "Name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Name (Required)</FormLabel>
            <FormControl>
              <Input placeholder="John Doe" {...field} className="bg-background" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      required: "Phone number is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Phone Number (Required)</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="(863) 555-0123" {...field} className="bg-background" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Email (Optional)</FormLabel>
            <FormControl>
              <Input type="email" placeholder="john@example.com" {...field} className="bg-background" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="notes" render={({
      field
    }) => <FormItem>
            <FormLabel>Notes (Optional)</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us anything helpful about your bathroom remodel. Optional." className="min-h-[120px] bg-background resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-semibold bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-md transition-all">
        {isSubmitting ? "Submitting Request..." : "Get Your Free Quote"}
      </Button>

    <p className="text-xs text-center text-muted-foreground mt-4">
  By submitting this form, you agree that Lakeland Bath Remodel Pros may contact you by phone, text, or email about your bathroom remodel request. You also agree to our{" "}
  <Link to="/legal" className="underline hover:text-foreground transition-colors">
    Privacy Policy
  </Link>
  .
    </p>
    </Form>;
}
export default function QuoteFormContact() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const benefits = [{
    icon: <Ruler className="size-5 text-secondary" />,
    title: "Bathroom Remodel Planning in Lakeland, FL",
    description: "Tell us what you want to update, replace, or improve in your bathroom."
  }, {
    icon: <Shield className="size-5 text-secondary" />,
    title: "Shower Remodels, Tub Replacements, Tile, and Vanities",
    description: "Request help with showers, tubs, tile, flooring, vanities, fixtures, and layout changes."
  }, {
    icon: <CheckCircle2 className="size-5 text-secondary" />,
    title: "Free Bathroom Remodel Quote Request",
    description: "Share your project details and we’ll follow up about next steps and quote information."
  }];
  return <section id="quoteformcontact" className="bg-background py-16 md:py-24 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-[0.03] dark:opacity-[0.05]" aria-hidden="true">
        <div className="absolute top-0 left-0 w-full h-full bg-[linear-gradient(to_right,#808080_1px,transparent_1px),linear-gradient(to_bottom,#808080_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          
          <div ref={leftRef} className={`lg:col-span-5 xl:col-span-6 space-y-8 transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-secondary/10 border border-secondary/20 text-secondary-foreground text-sm font-medium mb-2">
                <span className="size-2 rounded-lg bg-secondary animate-pulse"></span>
                Lakeland & Polk County
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-foreground leading-tight tracking-tight">Bathroom Remodeling Lakeland FL<span className="text-transparent bg-clip-text bg-gradient-to-r from-foreground to-foreground/70" data-ast-id="f:/src/pages/Home/sections/QuoteFormContact.tsx|sig:45mm43" data-ast-start="6197" data-ast-end="6441" contenteditable="false"></span></h1>
              <p className="text-lg text-muted-foreground max-w-xl leading-relaxed">Planning a bathroom remodel in Lakeland, FL? Request a free quote for bathroom remodeling, shower remodels, tub replacements, tile, flooring, vanities, fixtures, and full bathroom renovations in Lakeland and nearby Polk County areas.</p>
            </div>

            <Separator className="bg-border/60" />

            <div className="space-y-6">
              {benefits.map((benefit, i) => <div key={i} data-index={i} className="flex gap-4">
                  <div className="shrink-0 mt-1 size-10 rounded-lg bg-secondary/10 border border-secondary/20 flex items-center justify-center">
                    {benefit.icon}
                  </div>
                  <div>
                    <h3 className="text-base font-default font-semibold text-foreground mb-1">
                      {benefit.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="pt-6">
              <div className="p-6 rounded-xl border border-border bg-card/50 shadow-sm flex items-center gap-5">
                <div className="size-12 rounded-lg bg-primary/5 flex items-center justify-center shrink-0">
                  <Phone className="size-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Prefer not to call?</p>
                  <p className="text-xl font-default font-bold text-foreground">Get a free quote</p>
                </div>
              </div>
            </div>
          </div>

          <div ref={rightRef} className={`lg:col-span-7 xl:col-span-6 transition-all duration-700 delay-200 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative">
              <div className="absolute -top-4 -right-4 size-24 border-t-2 border-r-2 border-secondary/30 rounded-tr-2xl pointer-events-none hidden md:block"></div>
              <div className="absolute -bottom-4 -left-4 size-24 border-b-2 border-l-2 border-secondary/30 rounded-bl-2xl pointer-events-none hidden md:block"></div>
              
              <Card className="relative border-border shadow-xl bg-card/95 backdrop-blur-sm overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-secondary/40 via-secondary to-secondary/40"></div>
                <CardHeader className="pb-6 border-b border-border/50 bg-muted/20">
                  <CardTitle className="text-2xl font-default">Request Your Free Quote</CardTitle>
                  <CardDescription className="text-base">Provide your information below and we’ll contact you about your bathroom remodel request.</CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <FormProvider formId="quote_form_bathroom_remodel" sectionName="QuoteFormContact" defaultValues={{
                  name: "",
                  email: "",
                  phone: "",
                  notes: ""
                }} validationRules={{
                  name: {
                    required: true
                  },
                  phone: {
                    required: true
                  },
                  email: {
                    required: false
                  },
                  notes: {
                    required: false
                  }
                }} submitText="Get Your Free Quote" successMessage="Thank you! Your quote request has been submitted. A local professional will contact you soon.">
                    <QuoteForm />
                  </FormProvider>
                </CardContent>
              </Card>
            </div>
          </div>

        </div>
      </div>
    </section>;
}